import type {
  AccessNodePathEntry,
  AccessPasswordHandling,
  AccessRecordStatus,
  AccessRequestStatus,
  AnswerEmailStatus,
} from '@accounts/shared';

import { STR } from '../../../i18n';
import type { ChipTone } from '../../../ui/Primitives';

/**
 * The access module's shared wording and tones, so a status reads and colours the same on
 * the Queue, the person page, the catalog drawer and the Matrix. Tones only ever ride
 * alongside the words — colour is never the only signal.
 */

export const RECORD_STATUS_TONE: Record<AccessRecordStatus, ChipTone> = {
  active: 'positive',
  needs_access: 'attention',
  requested: 'neutral',
  remove_access: 'danger',
  removed: 'expired',
};

export const REQUEST_STATUS_TONE: Record<AccessRequestStatus, ChipTone> = {
  pending: 'neutral',
  approved: 'positive',
  denied: 'danger',
  cancelled: 'expired',
};

/** An answer email's status (spec row 14). `captured` is not a fault: nothing was meant to leave. */
export const ANSWER_EMAIL_STATUS_TONE: Record<AnswerEmailStatus, ChipTone> = {
  pending: 'neutral',
  captured: 'neutral',
  sent: 'positive',
  failed: 'danger',
};

export function recordStatusLabel(status: AccessRecordStatus): string {
  return STR.accessCommon.recordStatus[status];
}

export function requestStatusLabel(status: AccessRequestStatus): string {
  return STR.accessCommon.requestStatus[status];
}

export function passwordHandlingLabel(mode: AccessPasswordHandling): string {
  return STR.accessCommon.passwordHandling[mode];
}

/** "17th Street · Amazon" — root first; "Not filed" when the row has no place. */
export function pathLabel(path: readonly AccessNodePathEntry[]): string {
  if (path.length === 0) return STR.accessCommon.notPlaced;
  return path.map((step) => step.name).join(STR.accessCommon.pathSeparator);
}

/** A catalog row's full name as people say it: its place, then its own name. */
export function itemLabel(path: readonly AccessNodePathEntry[], name: string): string {
  if (path.length === 0) return name;
  return `${pathLabel(path)}${STR.accessCommon.pathSeparator}${name}`;
}
